import React, { useMemo } from 'react'
import Link from 'next/link'
import cn from 'classnames'
import Image from 'next/image'
import Router from 'next/router'

import { TiptapViewer } from '@components/TiptapViewer'
import { UserAvatar } from '@components/UserAvatar'
import { abbreviateNumber, diffNow } from '@lib/utils'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useTheme } from '@hooks/contexts/ThemeContext/ThemeContext'
import { ThemeEnum } from '@hooks/contexts/ThemeContext/interface'
import { Avatar } from '@components/Avatar'
import { AdminBadgeIcon } from '@components/icons/AdminBadgeIcon'
import { ChevronDownIcon } from '@components/icons/ChevronDownIcon'
import { LikeFillIcon } from '@components/icons/LikeFillIcon'
import { ReplyIcon } from '@components/icons/ReplyIcon'
import { DropdownSelect } from '../../DropdownSelect/DropdownSelect'
import { CommentCardProps } from '../interface'
import CommentBox from './CommentBox'
import { DropdownSelectOptionType } from '../../DropdownSelect/interface'

const defaultCommentOptions: DropdownSelectOptionType[] = [
  {
    label: 'แก้ไข',
    value: 'edit',
  },
  {
    label: 'ลบ',
    value: 'delete',
  },
  {
    label: 'ซ่อน',
    value: 'hidden',
  },
  {
    label: 'บล็อก',
    value: 'block',
  },
]

const VisibleSection = ({
  className,
  isSubComment,
  isLastComment,
  comment,
  isDelete,
  handleClickReadmore,
  replyComment,
  rootId,
  likeComment,
  isLike,
  manageComment,
  isReadmore,
  editCommentId,
  onCancelEditComment,
  user,
  onEditCommentSubmit,
  authorUserId,
  isCommentAble,
  isUserHasBlockedComment,
  isDrawer,
  bookId,
  bookTitle,
  isShowBookTitle,
}: CommentCardProps) => {
  const {
    theme,
    isNightOrDarkTheme,
    isLightOrSepiaTheme,
    isDarkTheme,
    isLightTheme,
    isSepiaTheme,
  } = useTheme()
  const blockedUserModal = useModal({ modal: 'blockedUser' }, true)
  const commentOptions = useMemo(() => {
    // TODO: get book author user id from getBookReader
    let options: DropdownSelectOptionType[] = []
    if (user?.id === comment.user?.id) {
      options = defaultCommentOptions.filter(option =>
        ['edit', 'delete', 'hidden'].includes(option.value)
      )
    } else if (
      authorUserId === user?.id &&
      !comment?.user?.isAdmin &&
      comment.user
    ) {
      options = defaultCommentOptions.filter(option =>
        ['delete', 'hidden', 'block'].includes(option.value)
      )
    } else if (
      authorUserId === user?.id &&
      !comment?.user?.isAdmin &&
      !comment.user
    )
      options = defaultCommentOptions.filter(option =>
        ['delete', 'hidden'].includes(option.value)
      )

    return options
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment, user, isCommentAble])

  function showBlockedUserModal(message: string) {
    blockedUserModal.show({
      content: (
        <div className='w-[400px] text-center mobile:w-full'>{message}</div>
      ),
    })
  }

  return (
    <div id={`comment-${comment.id}`} className='flex flex-row items-start'>
      <div className={cn(className)} />
      <div
        className={cn({
          'absolute ml-[25px] mt-[25px] z-[1] border-l-[1px] border-gray-3 h-full mobile:mt-[15px]':
            isSubComment && !isLastComment,
          'opacity-10': isNightOrDarkTheme,
        })}
      />
      <div
        className={cn('flex flex-row justify-between items-start w-full', {
          'mobile:ml-[50px] space-x-[10px]': isSubComment,
        })}
      >
        {comment?.user?.id && comment?.user?.avatarImgPath && !isDelete ? (
          <div className='relative'>
            <UserAvatar
              className={cn(
                'min-w-[50px] w-[50px] h-[50px] object-cover rounded-full mr-[20px] mobile:mr-[10px]',
                {
                  'mobile:min-w-[30px] mobile:w-[30px] mobile:h-[30px] mobile:mr-0':
                    isSubComment,
                  '!opacity-70':
                    isDarkTheme &&
                    Router.pathname === '/story/[id]/chapter/[chapterId]',
                }
              )}
              slug={comment.user.profilePageSlug}
              isPermanentBan={comment.user.isPermanentBan}
              src={comment.user.avatarImgPath}
            />
            {(comment.user.isAdmin ||
              comment.user.email === 'admin@mareads.com') && (
              <div className='absolute bottom-0 desktop-tablet:right-[15px] mobile:right-[5px]'>
                <AdminBadgeIcon className='w-[21.5px] h-[21px]' />
              </div>
            )}
          </div>
        ) : !comment?.user ? (
          <div className='w-[50px] rounded-full overflow-hidden mr-[20px] mobile:mr-[10px] z-[1] shrink-0'>
            <div className='relative pt-[100%] w-full h-full'>
              <Image src='/images/avatar.png' layout='fill' />
            </div>
          </div>
        ) : (
          <div
            className={cn({
              'min-w-[50px] min-h-[50px] rounded-full mr-[20px] mobile:mr-[10px] z-[1] bg-gray-4 dark:bg-dark-8':
                !isDelete,
            })}
          >
            <div className='relative'>
              <Avatar
                className={cn('w-[50px]', {
                  '!opacity-70':
                    isDarkTheme &&
                    Router.pathname === '/story/[id]/chapter/[chapterId]',
                })}
                src='/images/avatar.png'
                alt='Default User avatar'
              />
              {(comment.user.isAdmin ||
                comment.user.email === 'admin@mareads.com') && (
                <div className='absolute bottom-0 desktop-tablet:right-[-5px] mobile:right-[5px]'>
                  <AdminBadgeIcon className='w-[21.5px] h-[21px]' />
                </div>
              )}
            </div>
          </div>
        )}
        <div
          className={cn(
            'relative dark:bg-dark-6 rounded-[20px] font-sarabun w-full overflow-auto',
            {
              'bg-gray-1': ThemeEnum.LIGHT === theme,
              'bg-sepia-3': ThemeEnum.SEPIA === theme,
              '!bg-dark-7':
                theme === ThemeEnum.DARK &&
                Router.pathname === '/story/[id]/chapter/[chapterId]',
            }
          )}
        >
          {editCommentId === comment.id ? (
            <CommentBox
              onSubmit={onEditCommentSubmit}
              onCancel={onCancelEditComment}
              mode='edit'
              initialContent={comment.content}
              className='rounded-[20px] bg-gray-4'
            />
          ) : (
            <div className='mx-[20px] mt-[20px]'>
              {isShowBookTitle && bookId && bookTitle && !isSubComment ? (
                <div className='mb-[15px]'>
                  <a
                    href={`/story/${bookId}`}
                    className={cn(
                      'font-sarabun text-base font-bold text-secondary dark:text-dark-10 break-words',
                      {
                        '!text-gray-16':
                          theme === ThemeEnum.DARK &&
                          Router.pathname === '/story/[id]/chapter/[chapterId]',
                      }
                    )}
                  >
                    {bookTitle}
                    {comment.bookChapter && (
                      <>
                        <div className='inline border-r border-gray-3 mx-[10px]' />
                        ตอน : {comment.bookChapter.chapterName}
                      </>
                    )}
                    <div
                      className={cn('border-b-[1px] w-full pb-[15px]', {
                        'border-white/10': isNightOrDarkTheme,
                        'border-gray-3': !isNightOrDarkTheme,
                      })}
                    />
                  </a>
                </div>
              ) : comment.bookChapter && !isSubComment ? (
                <div
                  className={cn(
                    'mb-[15px] font-sarabun text-[16px] font-bold text-secondary dark:text-dark-10 break-words',
                    {
                      '!text-gray-16':
                        theme === ThemeEnum.DARK &&
                        Router.pathname === '/story/[id]/chapter/[chapterId]',
                    }
                  )}
                >
                  ตอน : {comment.bookChapter.chapterName}
                  <div
                    className={cn('border-b-[1px] w-full pb-[15px]', {
                      'border-white/10': isNightOrDarkTheme,
                      'border-gray-3': !isNightOrDarkTheme,
                    })}
                  />
                </div>
              ) : null}

              {isDelete ? (
                <div className='flex justify-center text-[14px] text-secondary-1 dark:text-dark-10 font-sarabun font-right p-[10px]'>
                  ความคิดเห็นนี้ถูกลบโดยเจ้าของความคิดเห็น
                </div>
              ) : (
                <div
                  className={cn(
                    'grid tablet:grid-cols-4 mobile:grid-cols-1 mb-[15px]',
                    {
                      'desktop:grid-cols-4': isDrawer,
                      'desktop:grid-cols-5': !isDrawer,
                    }
                  )}
                >
                  <div>
                    <div
                      className={cn(
                        'flex text-[14px] dark:text-dark-10 font-medium',
                        {
                          '!text-gray-16':
                            isDarkTheme &&
                            Router.pathname ===
                              '/story/[id]/chapter/[chapterId]',
                          'text-secondary': isLightOrSepiaTheme,
                        }
                      )}
                    >
                      {comment.user?.id ? (
                        <Link
                          href={`/profile/${comment.user?.profilePageSlug}`}
                          prefetch={false}
                        >
                          <a className='mr-[5px] w-[100px] truncate hover:underline'>
                            {comment.user?.displayName || 'Anonymous'}
                          </a>
                        </Link>
                      ) : (
                        <p className='mr-[5px] w-[100px] truncate'>Anonymous</p>
                      )}

                      <DropdownSelect
                        options={commentOptions}
                        handleSelect={(value: string) =>
                          manageComment(comment?.id, value, comment?.user?.id)
                        }
                      />
                    </div>
                    <div
                      className={cn(
                        'text-[12px] dark:text-dark-10 font-right font-light pt-[5px]',
                        {
                          'text-secondary-1': isLightOrSepiaTheme,
                          '!text-gray-16':
                            isDarkTheme &&
                            Router.pathname ===
                              '/story/[id]/chapter/[chapterId]',
                        }
                      )}
                    >
                      {diffNow(comment.createdAt, 'ที่ผ่านมา', true, true)}
                    </div>
                  </div>
                  <div
                    className={cn(
                      ' text-[14px] mb-[30px] dark:text-dark-10 font-right mobile:mt-[15px] tablet:col-span-3',
                      {
                        'text-secondary': isLightOrSepiaTheme,
                        '!text-gray-16':
                          isDarkTheme &&
                          Router.pathname === '/story/[id]/chapter/[chapterId]',
                        'desktop:col-span-3': isDrawer,
                        'desktop:col-span-4': !isDrawer,
                      }
                    )}
                  >
                    <TiptapViewer
                      value={comment.content}
                      className='tiptap-comment'
                      showMoreButton
                    />
                  </div>
                  <div />
                  <div className='flex flex-row mobile:mt-[20px] mobile:justify-end desktop-tablet:col-span-2'>
                    {isCommentAble && (
                      <button
                        type='button'
                        onClick={() => {
                          if (user?.isBan) {
                            showBlockedUserModal(
                              'คุณไม่สามารถตอบกลับความคิดเห็นนี้ได้ เนื่องจากคุณถูกแบนการใช้งานเป็นเวลา 7 วัน'
                            )
                          } else if (isUserHasBlockedComment) {
                            showBlockedUserModal(
                              'คุณไม่สามารถตอบกลับความคิดเห็นนี้ได้ เนื่องจากคุณถูกบล็อกการแสดงความคิดเห็นโดยเจ้าของเรื่อง'
                            )
                          } else {
                            handleClickReadmore()
                            replyComment({
                              rootId,
                              name: comment.user?.displayName,
                              id: isSubComment ? comment.id : undefined,
                            })
                          }
                        }}
                        className={cn(
                          'flex space-x-[5px] cursor-pointer mr-[20px] dark:text-dark-10',
                          {
                            'text-secondary': isLightOrSepiaTheme,
                            '!text-gray-16':
                              isDarkTheme &&
                              Router.pathname ===
                                '/story/[id]/chapter/[chapterId]',
                          }
                        )}
                      >
                        <ReplyIcon width='24' height='24' />
                        <div className='text-[14px] font-mitr underline font-medium'>
                          ตอบกลับ
                        </div>
                      </button>
                    )}
                    <button
                      type='button'
                      onClick={() => {
                        if (user?.isBan) {
                          showBlockedUserModal(
                            'คุณไม่สามารถกดไลค์ความคิดเห็นนี้ได้ เนื่องจากคุณถูกแบนการใช้งานเป็นเวลา 7 วัน'
                          )
                        } else {
                          likeComment(comment.id, rootId)
                        }
                      }}
                      className='cursor-pointer'
                    >
                      <div
                        className={cn('flex', {
                          'text-secondary-1': !isLike && !isDarkTheme,
                          '!text-gray-16':
                            !isLike &&
                            isDarkTheme &&
                            Router.pathname ===
                              '/story/[id]/chapter/[chapterId]',
                          'text-primary': isLike && !isDarkTheme,
                          'text-blue-1': isLike && isNightOrDarkTheme,
                          '!opacity-50':
                            isLike &&
                            isDarkTheme &&
                            Router.pathname ===
                              '/story/[id]/chapter/[chapterId]',
                        })}
                      >
                        <LikeFillIcon width='24' height='24' />
                        <span className='text-[14px] font-sarabun font-right font-light'>
                          {abbreviateNumber(comment.likeCount)}
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {!!comment.childrenComment?.length && !isReadmore && !isSubComment && (
            <button
              type='button'
              onClick={handleClickReadmore}
              className={cn(
                'flex justify-center items-center space-x-[4px] w-full h-[50px] dark:text-blue-1 dark:bg-gray-17/20 left-0 rounded-b-[20px] font-sarabun text-[14px] font-bold cursor-pointer',
                {
                  'bg-gray-4 text-secondary-1': isLightTheme,
                  'bg-sepia-4 text-secondary-1': isSepiaTheme,
                  '!bg-gray-16/[0.3] text-blue-1/50':
                    isDarkTheme &&
                    Router.pathname === '/story/[id]/chapter/[chapterId]',
                }
              )}
            >
              <div>
                ดู {comment.childrenComment?.length.toLocaleString()}{' '}
                ความคิดเห็นย่อย
              </div>
              <div className='mt-[2px]'>
                <ChevronDownIcon width='18' height='18' />
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default VisibleSection
